/**
 * ComisionesConfigPanel.jsx — Ventas → Configuración → Vendedores y Comisiones
 * Modalidades de comisión: (a) por venta hecha por el mismo usuario (% del usuario),
 * (b) por el vendedor (entidad con rol Vendedor) seleccionado en la venta (% de la entidad, sobre el total),
 * (c) por producto seleccionado para un usuario/vendedor específico (reglas).
 */
import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { Plus, Trash2, Edit2, X, Search, Save, RefreshCw } from "lucide-react";
import { AuthContext } from "../App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500";
const LABEL = "block text-xs text-slate-400 mb-1";

export default function ComisionesConfigPanel() {
  const { token, activeEmpresaPropia, hasPermission, user } = useContext(AuthContext);
  const logoTipo = activeEmpresaPropia?.slug;
  const esAdmin = ["admin", "super_admin", "gerente"].includes(user?.role);
  const puedeEditar = esAdmin || hasPermission("vendedores.editar") || hasPermission("vendedores.configurar_comisiones");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);
  const q = logoTipo ? `?logo_tipo=${encodeURIComponent(logoTipo)}` : "";

  const [cfg, setCfg] = useState({ modo_venta: true, modo_vendedor: true, modo_producto: false, reglas: [] });
  const [usuarios, setUsuarios] = useState([]);
  const [editando, setEditando] = useState(null);   // regla en edición (nueva o existente)
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/admin/comisiones-ventas/config${q}`, { headers });
      if (r.ok) setCfg(await r.json());
      const ru = await fetch(`${API}/admin/comisiones-ventas/comisionados${q}`, { headers });
      if (ru.ok) setUsuarios(await ru.json());
    } catch { /* ignorar */ }
  }, [token, q, headers]);
  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async (nuevo) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/comisiones-ventas/config${q}`, { method: "PUT", headers, body: JSON.stringify(nuevo) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error al guardar");
      setCfg(d); toast.success("Configuración de comisiones guardada");
      return true;
    } catch (e) { toast.error(e.message); return false; }
    finally { setSaving(false); }
  };

  const guardarRegla = async (regla) => {
    const reglas = regla.id && cfg.reglas.some(r => r.id === regla.id)
      ? cfg.reglas.map(r => (r.id === regla.id ? regla : r))
      : [...cfg.reglas, regla];
    if (await guardar({ ...cfg, reglas })) setEditando(null);
  };

  const generarPendientes = async () => {
    if (!window.confirm("Genera las comisiones de las facturas que ya estaban pagadas antes de activar esto (no duplica las existentes). ¿Continuar?")) return;
    try {
      const r = await fetch(`${API}/admin/comisiones-ventas/generar-pendientes${q}`, { method: "POST", headers, body: JSON.stringify({}) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      toast.success(`${d.comisiones} comisión(es) generadas de ${d.facturas} factura(s)`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-white text-sm font-semibold">Cómo se comisiona</h3>
        <p className="text-xs text-slate-400">
          La comisión se genera cuando la venta queda <b className="text-slate-200">totalmente pagada</b> y lleva la <b className="text-slate-200">fecha del pago</b>, no la de emisión de la factura.
          Se pagan desde Egresos → Comisiones. Podés combinar las modalidades: los renglones cubiertos por una regla de producto se comisionan por la regla y el resto por la venta.
        </p>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1" disabled={!puedeEditar} checked={!!cfg.modo_venta} onChange={e => guardar({ ...cfg, modo_venta: e.target.checked })} />
          <span className="text-sm text-slate-200">Por venta hecha por el mismo usuario
            <span className="block text-xs text-slate-500">Cada usuario cobra su % (el de la lista de usuarios de arriba) sobre las ventas que él registró, descontando Notas de Crédito.</span></span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1" disabled={!puedeEditar} checked={cfg.modo_vendedor !== false} onChange={e => guardar({ ...cfg, modo_vendedor: e.target.checked })} />
          <span className="text-sm text-slate-200">Por vendedor seleccionado en la venta
            <span className="block text-xs text-slate-500">El vendedor elegido en la factura (entidad con rol Vendedor del Catastro) cobra el % definido en su ficha sobre el total de la venta, descontando Notas de Crédito. Genera la comisión a pagar en Egresos → Comisiones.</span></span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1" disabled={!puedeEditar} checked={!!cfg.modo_producto} onChange={e => guardar({ ...cfg, modo_producto: e.target.checked })} />
          <span className="text-sm text-slate-200">Por producto seleccionado para un usuario específico
            <span className="block text-xs text-slate-500">Un usuario cobra un % por cada venta de ciertos productos (todos o algunos), sin importar quién hizo la venta.</span></span>
        </label>
      </div>

      {cfg.modo_producto && (
        <div className="bg-arandu-dark-light border border-white/10 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="text-sm font-medium text-white">Reglas por producto ({cfg.reglas.length})</span>
            {puedeEditar && <button onClick={() => setEditando({ usuario_id: "", todos_los_productos: false, producto_ids: [], producto_nombres: [], porcentaje: "", activa: true })}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs"><Plus className="w-3 h-3" /> Nueva regla</button>}
          </div>
          {cfg.reglas.length === 0 && <p className="p-6 text-center text-slate-500 text-sm">Sin reglas. El orden importa: si un producto está en varias reglas, gana la primera.</p>}
          <div className="divide-y divide-white/5">
            {cfg.reglas.map(r => (
              <div key={r.id} className="px-5 py-3 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="text-white">{r.usuario_nombre} — {r.porcentaje}% {!r.activa && <span className="text-xs text-slate-500">(inactiva)</span>}</p>
                  <p className="text-xs text-slate-400 truncate">{r.todos_los_productos ? "Todos los productos" : (r.producto_nombres || []).join(", ") || `${(r.producto_ids || []).length} producto(s)`}</p>
                </div>
                {puedeEditar && <>
                  <button className="p-1.5 text-slate-400 hover:text-white" onClick={() => setEditando(r)}><Edit2 className="w-4 h-4" /></button>
                  <button className="p-1.5 text-slate-400 hover:text-red-400" onClick={() => { if (window.confirm("¿Eliminar la regla?")) guardar({ ...cfg, reglas: cfg.reglas.filter(x => x.id !== r.id) }); }}><Trash2 className="w-4 h-4" /></button>
                </>}
              </div>
            ))}
          </div>
        </div>
      )}

      {puedeEditar && (
        <button onClick={generarPendientes} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-slate-300">
          <RefreshCw className="w-3 h-3" /> Generar comisiones de ventas ya cobradas (anteriores a esta función)
        </button>
      )}

      {editando && <ReglaModal regla={editando} usuarios={usuarios} logoTipo={logoTipo} headers={headers} saving={saving}
        onClose={() => setEditando(null)} onSave={guardarRegla} />}
    </div>
  );
}

function ReglaModal({ regla, usuarios, logoTipo, headers, saving, onClose, onSave }) {
  const [r, setR] = useState(regla);
  const [busqueda, setBusqueda] = useState("");
  const [resultados, setResultados] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!busqueda.trim()) { setResultados([]); return; }
    timer.current = setTimeout(async () => {
      const p = new URLSearchParams({ page: "1", page_size: "15" });
      if (logoTipo) p.set("logo_tipo", logoTipo);
      busqueda.split(/\s+/).filter(Boolean).forEach(t => p.append("terminos", t));
      const resp = await fetch(`${API}/admin/productos?${p}`, { headers });
      if (resp.ok) setResultados((await resp.json()).productos || []);
    }, 250);
    return () => clearTimeout(timer.current);
  }, [busqueda, logoTipo, headers]);

  const agregar = (p) => {
    if (r.producto_ids.includes(p.id)) return;
    setR(prev => ({ ...prev, producto_ids: [...prev.producto_ids, p.id], producto_nombres: [...(prev.producto_nombres || []), p.nombre] }));
    setBusqueda(""); setResultados([]);
  };
  const quitar = (id) => setR(prev => {
    const i = prev.producto_ids.indexOf(id);
    return { ...prev, producto_ids: prev.producto_ids.filter(x => x !== id), producto_nombres: (prev.producto_nombres || []).filter((_, k) => k !== i) };
  });

  const ok = () => {
    if (!r.usuario_id) { toast.error("Elegí el usuario"); return; }
    const pct = parseFloat(r.porcentaje);
    if (isNaN(pct) || pct <= 0 || pct > 100) { toast.error("El % debe estar entre 0 y 100"); return; }
    if (!r.todos_los_productos && r.producto_ids.length === 0) { toast.error("Elegí productos o marcá 'Todos los productos'"); return; }
    const u = usuarios.find(x => x.id === r.usuario_id);
    onSave({ ...r, porcentaje: pct, usuario_nombre: u?.nombre || r.usuario_nombre });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold">{regla.id ? "Editar regla" : "Nueva regla"} de comisión por producto</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><label className={LABEL}>Usuario o vendedor que cobra la comisión</label>
            <select className={INPUT} value={r.usuario_id} onChange={e => setR({ ...r, usuario_id: e.target.value })}>
              <option value="">— Elegir —</option>{usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre}{u.tipo === "entidad" ? " (vendedor)" : ""}</option>)}
            </select></div>
          <div><label className={LABEL}>%</label><input type="number" min="0" max="100" step="0.1" className={INPUT} value={r.porcentaje} onChange={e => setR({ ...r, porcentaje: e.target.value })} /></div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
          <input type="checkbox" checked={r.todos_los_productos} onChange={e => setR({ ...r, todos_los_productos: e.target.checked })} /> Todos los productos
        </label>
        {!r.todos_los_productos && (
          <div className="space-y-2">
            <label className={LABEL}>Productos (podés elegir uno o varios)</label>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
              <input className={`${INPUT} pl-9`} placeholder="Buscar producto por nombre, SKU o código..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
              {resultados.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-arandu-dark border border-white/10 rounded-lg max-h-56 overflow-y-auto shadow-xl">
                  {resultados.map(p => (
                    <button key={p.id} onClick={() => agregar(p)} className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/5">
                      {p.nombre} {p.sku && <span className="text-xs text-slate-500">· {p.sku}</span>}
                    </button>))}
                </div>)}
            </div>
            <div className="flex flex-wrap gap-2">
              {r.producto_ids.map((id, i) => (
                <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500/15 text-blue-200 text-xs">
                  {(r.producto_nombres || [])[i] || id}<button onClick={() => quitar(id)}><X className="w-3 h-3" /></button></span>))}
            </div>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
          <input type="checkbox" checked={r.activa !== false} onChange={e => setR({ ...r, activa: e.target.checked })} /> Regla activa
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-slate-300">Cancelar</button>
          <button onClick={ok} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm text-white"><Save className="w-4 h-4" /> Guardar regla</button>
        </div>
      </div>
    </div>
  );
}
